import { useCustomizationLocales } from '@lib/customization/useCustomizationLocales';
import { useGetConfigQuery, useUpdateConfigMutation, type CustomizationLocales } from '@store/api/rootEndpoints';
import LocalesDrawer from './LocalesDrawer';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * The drawer that changes the server's customization languages, with the read and the write
 * it needs. It is the one way to change them and has two doors: the card on the server
 * configuration page, where the setting lives, and the translations of a text field, where
 * a missing language is noticed.
 */
const LocalesEditor: React.FC<Props> = ({ open, onClose }) => {
  const locales = useCustomizationLocales();
  const { data: config } = useGetConfigQuery();
  const [updateConfig, { isLoading }] = useUpdateConfigMutation();

  // The languages go alone: the API leaves a part of the config the body does not carry as it
  // is stored. The session settings this tab read can be hours old, and sent back with the
  // languages they undid a session length changed elsewhere since.
  const save = async (customizationLocales: CustomizationLocales) => {
    // Until the config is read the drawer shows the built-in set and not the server's, and a
    // save would put an edit of that in the server's place.
    if (!config) return;

    const result = await updateConfig({ customizationLocales });

    if (!result.error) onClose();
  };

  return <LocalesDrawer open={open} locales={locales} saving={isLoading} onClose={onClose} onSave={save} />;
};

export default LocalesEditor;
